/**
 * Shared QR template configuration.
 * Maps template names to their styling options for QR code generation.
 * Used by both preview and download components to ensure consistency.
 */

export const TEMPLATE_CONFIG = {
  basic: {
    dotsOptions: {
      color: '#000000',
    },
    cornersSquareOptions: {
      type: 'square',
      color: '#000000',
    },
    cornersDotOptions: {
      type: 'square',
      color: '#000000',
    },
  },
  gradient: {
    dotsOptions: {
      color: '#000000',
      gradient: {
        type: 'linear',
        rotation: 45,
        colorStops: [
          { offset: 0, color: '#7c3aed' },
          { offset: 1, color: '#ec4899' },
        ],
      },
    },
    cornersSquareOptions: {
      type: 'square',
      color: '#000000',
      gradient: {
        type: 'linear',
        rotation: 45,
        colorStops: [
          { offset: 0, color: '#7c3aed' },
          { offset: 1, color: '#ec4899' },
        ],
      },
    },
    cornersDotOptions: {
      type: 'square',
      color: '#000000',
      gradient: {
        type: 'linear',
        rotation: 45,
        colorStops: [
          { offset: 0, color: '#7c3aed' },
          { offset: 1, color: '#ec4899' },
        ],
      },
    },
  },
  rounded: {
    dotsOptions: {
      color: '#000000',
      type: 'rounded',
    },
    cornersSquareOptions: {
      type: 'extra-rounded',
      color: '#000000',
    },
    cornersDotOptions: {
      type: 'dot',
      color: '#000000',
    },
  },
  dots: {
    dotsOptions: {
      color: '#000000',
      type: 'dots',
    },
    cornersSquareOptions: {
      type: 'dot',
      color: '#000000',
    },
    cornersDotOptions: {
      type: 'dot',
      color: '#000000',
    },
  },
  modern: {
    dotsOptions: {
      color: '#000000',
      type: 'classy',
    },
    cornersSquareOptions: {
      type: 'extra-rounded',
      color: '#000000',
    },
    cornersDotOptions: {
      type: 'dot',
      color: '#000000',
    },
  },
  minimal: {
    dotsOptions: {
      color: '#6b7280',
      type: 'square',
    },
    cornersSquareOptions: {
      type: 'square',
      color: '#6b7280',
    },
    cornersDotOptions: {
      type: 'square',
      color: '#6b7280',
    },
  },
};

/**
 * Get template configuration for QR code styling.
 * Returns base configuration merged with template-specific styling options.
 * @param {string} templateName - Template identifier ('basic', 'rounded', 'dots', 'modern', 'minimal', 'gradient')
 * @param {string} foregroundColor - Primary color for the template (hex code)
 * @returns {object} Template styling configuration with corners and pattern options
 * @throws {Error} If template name is invalid
 */
export function getTemplateConfig(templateName, foregroundColor = '#000000') {
  if (!Object.prototype.hasOwnProperty.call(TEMPLATE_CONFIG, templateName)) {
    throw new Error(`Unknown template: ${templateName}`);
  }

  const baseConfig = {
    dotsOptions: {
      color: foregroundColor,
    },
    cornersSquareOptions: {
      type: 'square',
      color: foregroundColor,
    },
    cornersDotOptions: {
      type: 'square',
      color: foregroundColor,
    },
  };

  // Apply template-specific configurations
  switch (templateName) {
    case 'gradient':
      return {
        dotsOptions: {
          ...baseConfig.dotsOptions,
          gradient: {
            type: 'linear',
            rotation: 45,
            colorStops: [
              { offset: 0, color: '#7c3aed' },
              { offset: 1, color: '#ec4899' },
            ],
          },
        },
        cornersSquareOptions: {
          ...baseConfig.cornersSquareOptions,
          gradient: {
            type: 'linear',
            rotation: 45,
            colorStops: [
              { offset: 0, color: '#7c3aed' },
              { offset: 1, color: '#ec4899' },
            ],
          },
        },
        cornersDotOptions: {
          ...baseConfig.cornersDotOptions,
          gradient: {
            type: 'linear',
            rotation: 45,
            colorStops: [
              { offset: 0, color: '#7c3aed' },
              { offset: 1, color: '#ec4899' },
            ],
          },
        },
      };

    case 'rounded':
      return {
        dotsOptions: {
          ...baseConfig.dotsOptions,
          type: 'rounded',
        },
        cornersSquareOptions: {
          ...baseConfig.cornersSquareOptions,
          type: 'extra-rounded',
        },
        cornersDotOptions: {
          ...baseConfig.cornersDotOptions,
          type: 'dot',
        },
      };

    case 'dots':
      return {
        dotsOptions: {
          ...baseConfig.dotsOptions,
          type: 'dots',
        },
        cornersSquareOptions: {
          ...baseConfig.cornersSquareOptions,
          type: 'dot',
        },
        cornersDotOptions: {
          ...baseConfig.cornersDotOptions,
          type: 'dot',
        },
      };

    case 'modern':
      return {
        dotsOptions: {
          ...baseConfig.dotsOptions,
          type: 'classy',
        },
        cornersSquareOptions: {
          ...baseConfig.cornersSquareOptions,
          type: 'extra-rounded',
        },
        cornersDotOptions: {
          ...baseConfig.cornersDotOptions,
          type: 'dot',
        },
      };

    case 'minimal':
      return {
        dotsOptions: {
          ...baseConfig.dotsOptions,
          type: 'square',
          color: '#6b7280',
        },
        cornersSquareOptions: {
          ...baseConfig.cornersSquareOptions,
          type: 'square',
          color: '#6b7280',
        },
        cornersDotOptions: {
          ...baseConfig.cornersDotOptions,
          type: 'square',
          color: '#6b7280',
        },
      };

    case 'basic':
    default:
      return baseConfig;
  }
}
